import React, { useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { toast } from "sonner";
import { ShieldCheck, ClipboardCheck, RefreshCw, Check, CheckCheck, Undo2, Search, Edit3, Loader2, ExternalLink } from "lucide-react";
import ProtocolForm from "./ProtocolForm.jsx";
import PaedRouteDoseForm from "./PaedRouteDoseForm.jsx";
import DrugForm from "./forms/DrugForm.jsx";
import AntibioticGuideForm from "./forms/AntibioticGuideForm.jsx";
import QuickReferenceForm from "./forms/QuickReferenceForm.jsx";

// Resources that have a friendly form editor (vs. raw JSON fallback).
const FORM_EDITORS = {
  protocols: ProtocolForm,
  paediatric_drugs: PaedRouteDoseForm,
  drugs: DrugForm,
  antibiotic_guide: AntibioticGuideForm,
  quick_reference: QuickReferenceForm,
};

const RESOURCE_LABELS = {
  protocols: "Protocols",
  drugs: "Drugs",
  paediatric_drugs: "Paed route doses",
  antibiotic_guide: "Antibiotic guides",
  quick_reference: "Quick references",
};

// Live route for "View page" links (only protocols/drugs have detail routes).
const DETAIL_ROUTE = {
  protocols: (id) => `/app/protocols/${id}`,
  drugs: (id) => `/app/drugs/${id}`,
};

const EDIT_HINTS = {
  protocols: "Edit any field below — algorithm steps, dosing and text update live after saving.",
  paediatric_drugs: "Edit the dose for each route of administration. Add/remove routes (PO / IV / IM…), set the per-kg dose, frequency, max and route notes, then save and approve.",
  drugs: "Edit the drug's doses, route, cautions and monitoring below, then save and approve.",
  antibiotic_guide: "Edit first-line / alternative regimens, key notes, pregnancy and red flags, then save and approve.",
  quick_reference: "Edit the reference items (label / value / note), title and colour, then save and approve.",
};

export default function AdminReviewQueue() {
  const [summary, setSummary] = useState([]);
  const [resource, setResource] = useState("protocols");
  const [statusFilter, setStatusFilter] = useState("pending");
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  // Inline editor state
  const [editing, setEditing] = useState(null); // { id, title, doc, raw }
  const [loadingDoc, setLoadingDoc] = useState(false);
  const [saving, setSaving] = useState(false);

  // Bulk approve state
  const [bulk, setBulk] = useState(null); // { scope: "resource"|"all" }
  const [bulkRunning, setBulkRunning] = useState(false);

  const loadSummary = useCallback(() => {
    api.get("/admin/review/summary").then((r) => setSummary(r.data || [])).catch(() => {});
  }, []);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const params = { status: statusFilter };
      if (q.trim()) params.q = q.trim();
      const { data } = await api.get(`/admin/review/${resource}`, { params });
      setItems(data?.items || []);
      setTotal(data?.total || 0);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load review queue");
    } finally {
      setLoading(false);
    }
  }, [resource, statusFilter, q]);

  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { loadList(); }, [loadList]);

  const setApproved = async (id, approved) => {
    try {
      await api.post(`/admin/review/${resource}/${id}`, { approved });
      toast.success(approved ? "Marked clinician-approved" : "Moved back to pending");
      loadList();
      loadSummary();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update");
    }
  };

  // Totals for the bulk-approve confirmation.
  const resourcePending = (summary.find((s) => s.resource === resource)?.pending) || 0;
  const allPending = summary.reduce((n, s) => n + (s.pending || 0), 0);

  const runBulkApprove = async () => {
    if (!bulk) return;
    setBulkRunning(true);
    try {
      const body = bulk.scope === "resource" ? { resource } : {};
      const { data } = await api.post("/admin/review-bulk/approve", body);
      toast.success(`Approved ${data.approved} item${data.approved === 1 ? "" : "s"}`);
      setBulk(null);
      loadList();
      loadSummary();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk approve failed");
    } finally {
      setBulkRunning(false);
    }
  };

  const openEdit = async (item) => {
    setLoadingDoc(true);
    setEditing({ id: item.id, title: item.title, doc: null, raw: "" });
    try {
      const { data } = await api.get(`/admin/cms/${resource}/${encodeURIComponent(item.id)}`);
      setEditing({ id: item.id, title: item.title, doc: data, raw: JSON.stringify(data, null, 2) });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load content");
      setEditing(null);
    } finally {
      setLoadingDoc(false);
    }
  };

  const saveEdit = async () => {
    if (!editing) return;
    let payload;
    if (FORM_EDITORS[resource]) {
      payload = editing.doc || {};
    } else {
      try { payload = JSON.parse(editing.raw); }
      catch { toast.error("Invalid JSON — fix syntax before saving."); return; }
    }
    setSaving(true);
    try {
      await api.patch(`/admin/cms/${resource}/${encodeURIComponent(editing.id)}`, { doc: payload });
      toast.success("Saved — changes are live");
      setEditing(null);
      loadList();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="border-slate-200" data-testid="admin-review-panel">
      <CardContent className="p-6 md:p-8">
        <div className="flex items-center gap-2 mb-1">
          <ClipboardCheck className="w-5 h-5 text-[#111827]" />
          <h3 className="font-heading font-semibold text-lg text-[#111827]">Content review queue</h3>
        </div>
        <p className="text-xs text-slate-500 mb-5">
          Edit and sign off AI-drafted clinical content. Edits save live; approved pages show a "Clinician-approved" badge to subscribers.
        </p>

        {/* Summary cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          {summary.map((s) => (
            <button
              key={s.resource}
              onClick={() => setResource(s.resource)}
              className={`text-left rounded-lg border p-3 transition-colors ${
                resource === s.resource ? "border-[#0B5FFF] bg-[#0B5FFF]/5" : "border-slate-200 hover:bg-slate-50"
              }`}
              data-testid={`review-summary-${s.resource}`}
            >
              <div className="text-xs font-semibold text-slate-600">{RESOURCE_LABELS[s.resource] || s.resource}</div>
              <div className="mt-1 flex items-baseline gap-1">
                <span className="font-heading text-2xl font-extrabold text-amber-700 tabular-nums">{s.pending}</span>
                <span className="text-xs text-slate-400">pending</span>
              </div>
              <div className="text-[11px] text-emerald-700 flex items-center gap-1 mt-0.5">
                <ShieldCheck className="w-3 h-3" /> {s.approved}/{s.total} approved
              </div>
            </button>
          ))}
        </div>

        {/* Controls */}
        <div className="flex flex-col md:flex-row md:items-center gap-3 mb-4">
          <div className="flex items-center gap-2">
            <Label className="text-xs text-slate-500">Resource</Label>
            <Select value={resource} onValueChange={setResource}>
              <SelectTrigger className="w-44" data-testid="review-resource-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.keys(RESOURCE_LABELS).map((k) => (
                  <SelectItem key={k} value={k}>{RESOURCE_LABELS[k]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-xs text-slate-500">Show</Label>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-36" data-testid="review-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="all">All</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="relative flex-1 max-w-xs">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input
              placeholder="Search title…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-9"
              data-testid="review-search"
            />
          </div>
          <Button variant="outline" size="sm" onClick={() => { loadList(); loadSummary(); }} disabled={loading} data-testid="review-refresh">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </div>

        {/* Bulk approve actions */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-4">
          <Button
            size="sm"
            className="bg-emerald-600 hover:bg-emerald-700"
            disabled={resourcePending === 0}
            onClick={() => setBulk({ scope: "resource" })}
            data-testid="review-approve-all-resource"
          >
            <CheckCheck className="w-4 h-4 mr-1.5" />
            Approve all {RESOURCE_LABELS[resource]} ({resourcePending})
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="border-emerald-300 text-emerald-800 hover:bg-emerald-50"
            disabled={allPending === 0}
            onClick={() => setBulk({ scope: "all" })}
            data-testid="review-approve-all-content"
          >
            <ShieldCheck className="w-4 h-4 mr-1.5" />
            Approve ALL pending content ({allPending})
          </Button>
        </div>

        <div className="text-xs text-slate-500 mb-2">{total} item{total === 1 ? "" : "s"}</div>

        {items.length === 0 ? (
          <div className="text-sm text-slate-500 text-center py-10">
            {loading ? "Loading…" : "Nothing here. 🎉"}
          </div>
        ) : (
          <div className="divide-y divide-slate-100 border border-slate-200 rounded-lg">
            {items.map((it) => (
              <div key={it.id} className="flex items-center justify-between gap-3 p-3" data-testid={`review-item-${it.id}`}>
                <div className="min-w-0">
                  <div className="font-medium text-sm text-[#111827] truncate">{it.title || it.id}</div>
                  <div className="text-[11px] text-slate-500 flex items-center gap-2 flex-wrap">
                    {it.category && <span className="capitalize">{(it.category || "").replace(/_/g, " ")}</span>}
                    {it.review_status === "approved" ? (
                      <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">
                        Approved{it.reviewed_at ? ` · ${new Date(it.reviewed_at).toLocaleDateString("en-ZA")}` : ""}
                        {it.reviewed_by ? ` · ${it.reviewed_by}` : ""}
                      </Badge>
                    ) : (
                      <Badge className="bg-amber-100 text-amber-800 border-amber-200">Pending review</Badge>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <Button size="sm" variant="outline" onClick={() => openEdit(it)} data-testid={`review-edit-${it.id}`}>
                    <Edit3 className="w-3.5 h-3.5 mr-1" /> Edit
                  </Button>
                  {it.review_status === "approved" ? (
                    <Button size="sm" variant="outline" onClick={() => setApproved(it.id, false)} data-testid={`unapprove-${it.id}`}>
                      <Undo2 className="w-3.5 h-3.5 mr-1" /> Un-approve
                    </Button>
                  ) : (
                    <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => setApproved(it.id, true)} data-testid={`approve-${it.id}`}>
                      <Check className="w-3.5 h-3.5 mr-1" /> Approve
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between gap-3 pr-6">
              <DialogTitle className="truncate">Edit · {editing?.title || editing?.id}</DialogTitle>
              {editing && DETAIL_ROUTE[resource] && (
                <a
                  href={DETAIL_ROUTE[resource](editing.id)}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs text-[#111827] hover:underline inline-flex items-center gap-1 flex-shrink-0"
                  data-testid="review-view-page"
                >
                  <ExternalLink className="w-3.5 h-3.5" /> View live page
                </a>
              )}
            </div>
          </DialogHeader>

          {loadingDoc || !editing?.doc ? (
            <div className="py-10 text-center text-slate-500 text-sm">
              <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading content…
            </div>
          ) : FORM_EDITORS[resource] ? (
            <>
              <p className="text-[11px] text-slate-500">
                {EDIT_HINTS[resource] || "Edit the fields below — changes save live after approving and lock the item from the periodic sync."}
              </p>
              {React.createElement(FORM_EDITORS[resource], {
                value: editing.doc,
                onChange: (doc) => setEditing((s) => ({ ...s, doc })),
              })}
            </>
          ) : (
            <>
              <p className="text-[11px] text-slate-500">
                Full record as JSON. Edit the values you need and save — changes go live immediately and lock the item from the periodic sync.
              </p>
              <Textarea
                value={editing.raw}
                onChange={(e) => setEditing((s) => ({ ...s, raw: e.target.value }))}
                rows={18}
                className="font-mono text-xs"
                data-testid="review-json-editor"
              />
            </>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={saveEdit} disabled={saving || loadingDoc || !editing?.doc} className="bg-[#0B5FFF] hover:bg-[#0A54E0]" data-testid="review-save-edit">
              {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</> : "Save changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk approve confirm dialog */}
      <Dialog open={!!bulk} onOpenChange={(o) => !o && setBulk(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {bulk?.scope === "all" ? "Approve all pending content?" : `Approve all ${RESOURCE_LABELS[resource]}?`}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            This marks{" "}
            <strong>{bulk?.scope === "all" ? allPending : resourcePending}</strong>{" "}
            {bulk?.scope === "all" ? "item(s) across every content type" : `${RESOURCE_LABELS[resource]?.toLowerCase()}`}{" "}
            as <span className="text-emerald-700 font-medium">clinician-approved</span> in one go. Approved pages immediately show the "Clinician-approved" badge to subscribers.
          </p>
          <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900">
            You are signing off this content clinically. You can still un-approve or edit any item afterwards.
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulk(null)} disabled={bulkRunning}>Cancel</Button>
            <Button onClick={runBulkApprove} disabled={bulkRunning} className="bg-emerald-600 hover:bg-emerald-700" data-testid="review-bulk-approve-confirm">
              {bulkRunning ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Approving…</> : <><CheckCheck className="w-4 h-4 mr-1.5" /> Approve all</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
